import { useEffect, useRef, useState } from "react";
import { Calculator, X } from "@phosphor-icons/react";
import FuelTools from "@/components/FuelTools";

/**
 * QuickCalculator — V1.1.1 OP-006 · header widget.
 *
 * A minimal, always-there calculator so dispatch never has to leave the
 * app to compute litres, taxes, discounts, tank fills, or delivery
 * estimates. Deliberately not a full RPN calculator — it's an office
 * companion, not a scientific tool.
 *
 * Design:
 *   • Header icon toggles an anchored popover.
 *   • Expression grammar: JavaScript-like arithmetic with % handled as
 *     "divide by 100" (so "13" → tax percent inputs feel natural).
 *   • Enter evaluates. Escape closes. Backspace deletes one char.
 *   • Never uses `eval()` — a small recursive-descent parser handles + - * / % and parentheses.
 *
 * The state deliberately doesn't persist across a page refresh — this is
 * a scratch pad, not a saved workspace.
 */
export default function QuickCalculator() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("calc");
  const [expr, setExpr] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const evaluate = () => {
    const trimmed = expr.trim();
    if (!trimmed) {
      setResult(null);
      setError(null);
      return;
    }
    try {
      const value = safeEval(trimmed);
      setResult(fmt(value));
      setError(null);
    } catch (e) {
      setError(e.message || "Invalid expression");
      setResult(null);
    }
  };

  useEffect(() => {
    if (open) {
      // Delay focus so the popover has attached to the DOM.
      const t = setTimeout(() => inputRef.current?.focus(), 20);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    const onDoc = (e) => {
      if (open && rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const insert = (token) => {
    setExpr((prev) => prev + token);
    inputRef.current?.focus();
  };

  const clear = () => {
    setExpr("");
    setResult(null);
    setError(null);
  };

  const applyResult = () => {
    if (result != null) {
      setExpr(String(result));
      setResult(null);
    }
  };

  const KEYS = [
    ["7", "8", "9", "/"],
    ["4", "5", "6", "*"],
    ["1", "2", "3", "-"],
    ["0", ".", "%", "+"],
  ];

  return (
    <div ref={rootRef} className="relative">
      <button
        data-testid="quick-calculator-button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Quick calculator"
        title="Quick calculator"
        className="inline-flex h-10 w-10 items-center justify-center rounded-md text-ink-secondary hover:bg-surface-alt"
      >
        <Calculator size={20} weight={open ? "fill" : "regular"} />
      </button>

      {open && (
        <div
          data-testid="quick-calculator-popover"
          className="absolute right-0 top-12 z-40 w-80 overflow-hidden rounded-md border border-divider bg-surface shadow-lg"
        >
          <div className="flex items-center justify-between border-b border-divider px-3 py-2">
            <div className="flex gap-1">
              <button
                data-testid="quick-calculator-tab-calc"
                onClick={() => setTab("calc")}
                className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${
                  tab === "calc" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                Calculator
              </button>
              <button
                data-testid="quick-calculator-tab-fuel"
                onClick={() => setTab("fuel")}
                className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${
                  tab === "fuel" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                Fuel Tools
              </button>
            </div>
            <button
              data-testid="quick-calculator-close"
              onClick={() => setOpen(false)}
              className="rounded-md p-1 text-ink-secondary hover:bg-surface-alt"
              aria-label="Close"
            >
              <X size={12} weight="bold" />
            </button>
          </div>

          {tab === "fuel" ? (
            <FuelTools />
          ) : (
          <div className="p-3">
            <input
              ref={inputRef}
              data-testid="quick-calculator-input"
              type="text"
              value={expr}
              onChange={(e) => {
                setExpr(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); evaluate(); }
                if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
              }}
              placeholder="e.g. 500 * 1.13"
              className="w-full rounded-md border border-divider bg-surface-alt px-2 py-1.5 text-right font-mono text-sm text-ink-primary focus:border-brand focus:outline-none"
            />
            <div
              data-testid="quick-calculator-result"
              className={`mt-1 min-h-[24px] text-right font-mono text-lg font-semibold ${
                error ? "text-status-error-fg" : "text-ink-primary"
              }`}
            >
              {error ? error : result != null ? `= ${result}` : "\u00A0"}
            </div>

            <div className="mt-2 grid grid-cols-4 gap-1">
              {KEYS.flat().map((k) => (
                <button
                  key={k}
                  data-testid={`quick-calculator-key-${k}`}
                  onClick={() => insert(k)}
                  className="rounded-md border border-divider bg-surface px-1 py-1.5 text-sm font-medium text-ink-primary hover:bg-surface-alt"
                >
                  {k}
                </button>
              ))}
            </div>

            <div className="mt-1 grid grid-cols-4 gap-1">
              <button
                data-testid="quick-calculator-clear"
                onClick={clear}
                className="rounded-md bg-surface-alt px-1 py-1.5 text-xs font-semibold text-ink-primary hover:bg-divider"
              >
                C
              </button>
              <button
                data-testid="quick-calculator-backspace"
                onClick={() => setExpr((p) => p.slice(0, -1))}
                className="rounded-md bg-surface-alt px-1 py-1.5 text-xs font-semibold text-ink-primary hover:bg-divider"
              >
                ⌫
              </button>
              <button
                data-testid="quick-calculator-apply"
                onClick={applyResult}
                disabled={result == null}
                className="rounded-md bg-surface-alt px-1 py-1.5 text-xs font-semibold text-ink-primary hover:bg-divider disabled:opacity-40"
              >
                Ans
              </button>
              <button
                data-testid="quick-calculator-equals"
                onClick={evaluate}
                className="rounded-md bg-brand px-1 py-1.5 text-xs font-semibold text-brand-foreground hover:bg-brand-hover"
              >
                =
              </button>
            </div>

            <div className="mt-2 text-[10px] text-ink-secondary">
              Tip: use <span className="font-mono">%</span> for percent
              (e.g. <span className="font-mono">500 + 13%</span> → 565).
            </div>
          </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// safeEval — recursive-descent parser for + - * / % and parentheses.
// Never uses JS eval(). Returns a Number. Throws with a clear message on
// unexpected input.
//
// Percent handling (mirrors what a physical office calculator does):
//   • "13%"        → 0.13
//   • "500 + 13%"  → 565    (a + b% → a + a*b/100)
//   • "500 - 10%"  → 450    (a - b% → a - a*b/100)
//   • "500 * 13%"  →  65    (a * b% → a * b/100)
//   • "500 / 13%"  → ~3846  (a / b% → a / (b/100))
//
// Implementation note: the parser threads a small {value, isPercent} tuple
// up through parseAtom / parseTerm / parseExpression so that a trailing '%'
// can be interpreted differently depending on the operator that precedes
// it. Handling percent as a standalone divide-by-100 inside parseFactor
// (as the first version did) hides the '%' token from the enclosing
// +/- expression and gives the wrong answer for "a + b%".
// ---------------------------------------------------------------------------
export function safeEval(input) {
  const tokens = tokenize(input);
  const parser = { tokens, i: 0 };
  const result = parseExpression(parser);
  if (parser.i < parser.tokens.length) {
    throw new Error("Unexpected token");
  }
  const value = result.isPercent ? result.value / 100 : result.value;
  if (!Number.isFinite(value)) throw new Error("Divide by zero");
  return value;
}

function tokenize(s) {
  const out = [];
  let i = 0;
  while (i < s.length) {
    const c = s[i];
    if (/\s/.test(c)) { i++; continue; }
    if (/[0-9.]/.test(c)) {
      let j = i + 1;
      while (j < s.length && /[0-9.]/.test(s[j])) j++;
      out.push({ type: "num", value: Number(s.slice(i, j)) });
      i = j; continue;
    }
    if ("+-*/%()".includes(c)) {
      out.push({ type: c });
      i++;
      continue;
    }
    throw new Error(`Unexpected character '${c}'`);
  }
  return out;
}

function peek(p) { return p.tokens[p.i]; }
function consume(p, type) {
  const t = peek(p);
  if (!t || t.type !== type) throw new Error(`Expected '${type}'`);
  p.i++;
  return t;
}

function parseExpression(p) {
  let left = parseTerm(p);
  while (peek(p) && (peek(p).type === "+" || peek(p).type === "-")) {
    const op = peek(p).type; p.i++;
    const right = parseTerm(p);
    const leftVal = left.isPercent ? left.value / 100 : left.value;
    let newVal;
    if (right.isPercent) {
      // a op b%  →  a op (a * b / 100)
      const delta = (leftVal * right.value) / 100;
      newVal = op === "+" ? leftVal + delta : leftVal - delta;
    } else {
      newVal = op === "+" ? leftVal + right.value : leftVal - right.value;
    }
    left = { value: newVal, isPercent: false };
  }
  return left;
}

function parseTerm(p) {
  let left = parseAtom(p);
  while (peek(p) && (peek(p).type === "*" || peek(p).type === "/")) {
    const op = peek(p).type; p.i++;
    const right = parseAtom(p);
    // "a * b%" → a * (b/100); "a / b%" → a / (b/100)
    const leftVal = left.isPercent ? left.value / 100 : left.value;
    const rightVal = right.isPercent ? right.value / 100 : right.value;
    const combined = op === "*" ? leftVal * rightVal : leftVal / rightVal;
    left = { value: combined, isPercent: false };
  }
  return left;
}

function parseAtom(p) {
  const t = peek(p);
  if (!t) throw new Error("Unexpected end of input");
  if (t.type === "-") { p.i++; const a = parseAtom(p); return { value: -a.value, isPercent: a.isPercent }; }
  if (t.type === "+") { p.i++; return parseAtom(p); }
  if (t.type === "(") {
    p.i++;
    const inner = parseExpression(p);
    consume(p, ")");
    return maybePercent(p, inner.isPercent ? inner.value / 100 : inner.value);
  }
  if (t.type === "num") {
    p.i++;
    return maybePercent(p, t.value);
  }
  throw new Error("Expected a number or '('");
}

function maybePercent(p, value) {
  if (peek(p) && peek(p).type === "%") {
    p.i++;
    return { value, isPercent: true };
  }
  return { value, isPercent: false };
}

function fmt(n) {
  if (Number.isInteger(n)) return String(n);
  // Trim to at most 6 decimal places, drop trailing zeros.
  return Number(n.toFixed(6)).toString();
}
